import {Observable} from 'rxjs';
import {
    CpmCProjectCreateApiSchema,
    CpmCProjectUpdateApiSchema
} from '../../schemas/c-customer/cpmc-project.api-schemas';
import {CpmCApiBridge} from '../../../../../src/app/cpmc-api-bridge';
import {CpmCQueryPaginatorConfig, CpmCQueryPaginatorConfigUtils} from '../../cpmc-defines';
import {CpmCProjectApiModel} from '../../models/c-customer/cpmc-project.api-model';
import {AllowedQueryParamTypes, QueryParams} from '../../../../../sdk/src/utils/query.utils';
import {CpmCPaginatedResultAPISchema} from '../../schemas/cpmc-paginated-result.api-schemas';

export type CpmCGetProjectsParams = {
    [K in keyof CpmCProjectApiModel]?: CpmCProjectApiModel[K] extends AllowedQueryParamTypes
        ? CpmCProjectApiModel[K]
        : never;
} & {} & QueryParams;

/**
 * Project Api Service
 */
export class CpmCProjectApiService {
    public static readonly dPaginatorConfig: CpmCQueryPaginatorConfig = {limit: -1, sort: 'name', order: 1};

    // ===============================================================
    // ===============================================================

    public static baseUrl(inCustomerUUID: string | undefined) {
        const base = CpmCApiBridge.getBackendBaseURL() + 'api/core/v1/';
        const customerUUID = CpmCApiBridge.getCurrentCustomerUUID();

        return base + `customer/${customerUUID || inCustomerUUID}/project`;
    }

    // ===============================================================
    // ===============================================================

    public static endpointList(inCustomerUUID: string | undefined): string {
        return `${CpmCProjectApiService.baseUrl(inCustomerUUID)}/list`;
    }
    public static endpointNew(inCustomerUUID: string | undefined): string {
        return `${CpmCProjectApiService.baseUrl(inCustomerUUID)}`;
    }
    public static endpointGet(inCustomerUUID: string | undefined, projectUUID: string): string {
        return `${CpmCProjectApiService.baseUrl(inCustomerUUID)}/${projectUUID}`;
    }
    public static endpointEdit(inCustomerUUID: string | undefined, projectUUID: string): string {
        return `${CpmCProjectApiService.baseUrl(inCustomerUUID)}/${projectUUID}`;
    }
    public static endpointDelete(inCustomerUUID: string | undefined, projectUUID: string): string {
        return `${CpmCProjectApiService.baseUrl(inCustomerUUID)}/${projectUUID}`;
    }

    // ===============================================================
    // ===============================================================

    public static getProjectList(
        params: CpmCGetProjectsParams = {},
        paginatorConfig: CpmCQueryPaginatorConfig = CpmCProjectApiService.dPaginatorConfig
    ): Observable<CpmCPaginatedResultAPISchema<CpmCProjectApiModel>> {
        return CpmCApiBridge.httpGet<CpmCPaginatedResultAPISchema<CpmCProjectApiModel>>(
            CpmCProjectApiService.endpointList(undefined),
            {
                ...(params as QueryParams),
                ...CpmCQueryPaginatorConfigUtils.toQueryParams(paginatorConfig)
            }
        );
    }
    public static getProjectByUUID(projectUUID: string): Observable<CpmCProjectApiModel> {
        return CpmCApiBridge.httpGet<CpmCProjectApiModel>(CpmCProjectApiService.endpointGet(undefined, projectUUID));
    }
    public static createProject(payload: CpmCProjectCreateApiSchema): Observable<CpmCProjectApiModel> {
        return CpmCApiBridge.httpPost<CpmCProjectApiModel>(CpmCProjectApiService.endpointNew(undefined), payload);
    }
    public static updateProject(
        projectUUID: string,
        payload: CpmCProjectUpdateApiSchema
    ): Observable<CpmCProjectApiModel> {
        return CpmCApiBridge.httpPut<CpmCProjectApiModel>(
            CpmCProjectApiService.endpointEdit(undefined, projectUUID),
            payload
        );
    }
    public static deleteProject(projectUUID: string): Observable<boolean> {
        return CpmCApiBridge.httpDelete<boolean>(CpmCProjectApiService.endpointDelete(undefined, projectUUID));
    }
}
